import { CoreModule, CONTENT_SECURITY_STRATEGY } from '@abp/ng.core';
import { SettingManagementConfigModule } from '@abp/ng.setting-management/config';
import { provideAbpThemeShared, ThemeSharedModule, withValidationBluePrint } from '@abp/ng.theme.shared';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IdentityConfigModule } from '@volo/abp.ng.identity/config';
import { CommercialUiConfigModule } from '@volo/abp.commercial.ng.ui/config';
import { AccountAdminConfigModule } from '@volo/abp.ng.account/admin/config';
import { AccountPublicConfigModule } from '@volo/abp.ng.account/public/config';
import { GdprConfigModule } from '@volo/abp.ng.gdpr/config';
import { AuditLoggingConfigModule } from '@volo/abp.ng.audit-logging/config';
import { LanguageManagementConfigModule } from '@volo/abp.ng.language-management/config';
import { registerLocale } from '@volo/abp.ng.language-management/locale';
import { FileManagementConfigModule } from '@volo/abp.ng.file-management/config';
import { ChatConfigModule } from '@volo/abp.ng.chat/config';
import { SaasConfigModule } from '@volo/abp.ng.saas/config';
import { TextTemplateManagementConfigModule } from '@volo/abp.ng.text-template-management/config';
import { OpeniddictproConfigModule } from '@volo/abp.ng.openiddictpro/config';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { APP_ROUTE_PROVIDER } from './route.provider';
import { FeatureManagementModule } from '@abp/ng.feature-management';
import {
  HttpErrorComponent,
  ThemeLeptonXModule,
} from '@volosoft/abp.ng.theme.lepton-x';
import { SideMenuLayoutModule } from '@volosoft/abp.ng.theme.lepton-x/layouts';
import { AccountLayoutModule } from '@volosoft/abp.ng.theme.lepton-x/account';
import { AbpOAuthModule } from '@abp/ng.oauth';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AppVersionService } from './shared/services/app-version-service'; 
import { ErrorHandlerService } from './shared/services/error-handler-service';
import { FaroInitializerService } from './shared/observability/faro-initializer-service';
import { ReactiveFormsModule } from '@angular/forms';
import { DBConfig, NgxIndexedDBModule } from 'ngx-indexed-db';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { CustomErrorInterceptor } from './services/httpInterceptor.interceptor'
import { RequestDelayInterceptor } from './services/request-delay.interceptor';
import { ServerClockInterceptor } from './services/server-clock.interceptor';
import { LPX_TRANSLATE_SERVICE_TOKEN } from '@volo/ngx-lepton-x.core';
import { LeptonXTranslateService } from './shared/providers/leptonx-translate.service';
const faroInitializer = (faroInitializer: FaroInitializerService) => () => faroInitializer;

export function cspFactory(): any {
  // grab the nonce that you injected via your Razor Layout <meta name="csp-nonce" …>
  const nonce = document
    .querySelector('meta[name="csp-nonce"]')
    ?.getAttribute('content');

  // use the built-in Loose strategy, which will stamp that nonce on any inline scripts/styles
  return CONTENT_SECURITY_STRATEGY.Loose(nonce);
}

const dbConfig: DBConfig = {
  name: 'TrainingTrackingDB',
  // 9: adds `apiResponses` (replaces `latestResponses`, keyed by store name so writing it is a
  // plain upsert) and `deadLetterOps` (queued operations that can never be applied).
  version: 9,
  objectStoresMeta: [
    {
      store: 'trainingPersons',
      storeConfig: { keyPath: 'id', autoIncrement: true },
      storeSchema: [
        { name: 'document', keypath: 'document', options: { unique: false } },
        { name: 'trainingId', keypath: 'trainingId', options: { unique: false } },
        { name: 'type', keypath: 'type', options: { unique: false } },
      ]
    },
    {
      store: 'trainingPersonsToUnassign',
      storeConfig: { keyPath: 'id', autoIncrement: true },
      storeSchema: [
        { name: 'personId', keypath: 'personId', options: { unique: false } },
        { name: 'trainingId', keypath: 'trainingId', options: { unique: false } },
        { name: 'type', keypath: 'type', options: { unique: false } },
      ]
    },
    //Open new trainings
    {
      store: 'selectOptions',
      storeConfig: {keyPath: 'selectKey', autoIncrement: false},
      storeSchema: [
        {name: "selectKey", keypath: "selectKey", options: {unique:true} },
        {name: "options", keypath: "options", options: {unique:false} },
      ]
    },
    {
      store: 'newTraining',
      storeConfig: {keyPath: 'id', autoIncrement: true},
      storeSchema: [
        {name: 'unitId', keypath: 'unitId', options: {unique:false}},
        {name: 'areaId', keypath: 'areaId', options: {unique:false}},
        {name: 'topicId', keypath: 'topicId', options: {unique:false}},
        {name: 'trainingTemplateId', keypath: 'trainingTemplateId', options: {unique:false}},
        {name: 'durationMinutes', keypath: 'durationMinutes', options: {unique:false}},
        {name: 'topicContent', keypath: 'topicContent', options: {unique:false}},
        {name: 'startDate', keypath: 'startDate', options: {unique:false}},
        {name: 'endDate', keypath: 'endDate', options: {unique:false}},
      ]
    },
    {
      store: 'endedTrainings',
      storeConfig: { keyPath: 'id', autoIncrement: true },
      storeSchema: [
        { name: 'trainingId', keypath: 'trainingId', options: { unique: false } },
        { name: 'endDate', keypath: 'endDate', options: { unique: false } },
      ]
    },
    {
      store: 'latestResponses',
      storeConfig: { keyPath: 'id', autoIncrement: true },
      storeSchema: [
        { name: 'store', keypath: 'store', options: { unique: true } },
        { name: 'data', keypath: 'data', options: { unique: false } },
      ]
    },
    {
      store: 'unsyncedOps',
      storeConfig: { keyPath: 'id', autoIncrement: true },
      storeSchema: [
        { name: 'type', keypath: 'type', options: { unique: false } },
        { name: 'ops', keypath: 'ops', options: { unique: false } },
        { name: 'dto', keypath: 'dto', options: { unique: false } },
      ]
    },
    // Cached list responses, keyed by store name: `update` is an upsert, so concurrent writes
    // cannot collide the way they did on `latestResponses` (autoIncrement id + unique index).
    {
      store: 'apiResponses',
      storeConfig: { keyPath: 'store', autoIncrement: false },
      storeSchema: [
        { name: 'store', keypath: 'store', options: { unique: true } },
        { name: 'data', keypath: 'data', options: { unique: false } },
      ]
    },
    // Queued operations that can never be applied (conflict, missing entity, attempts exhausted).
    // Kept for inspection instead of being replayed on every app start.
    {
      store: 'deadLetterOps',
      storeConfig: { keyPath: 'id', autoIncrement: true },
      storeSchema: [
        { name: 'store', keypath: 'store', options: { unique: false } },
        { name: 'status', keypath: 'status', options: { unique: false } },
        { name: 'failedAt', keypath: 'failedAt', options: { unique: false } },
      ]
    }
  ]
}

@NgModule({
  declarations: [AppComponent],
  imports: [

  BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule.forRoot({
      environment,
      registerLocaleFn: registerLocale(),
    }),
    AbpOAuthModule.forRoot(),
    ThemeSharedModule.forRoot({
      httpErrorConfig: {
        errorScreen: {
          component: HttpErrorComponent,
          forWhichErrors: [401, 403, 404, 500],
          hideCloseIcon: true,
        },
      },
    }),
    AccountAdminConfigModule.forRoot(),
    AccountPublicConfigModule.forRoot(),
    CommercialUiConfigModule.forRoot(),
    GdprConfigModule.forRoot({
      privacyPolicyUrl: 'gdpr-cookie-consent/privacy',
      cookiePolicyUrl: 'gdpr-cookie-consent/cookie',
    }),
    LanguageManagementConfigModule.forRoot(),
    FileManagementConfigModule.forRoot(),
    SaasConfigModule.forRoot(),
    ChatConfigModule.forRoot(),
    AuditLoggingConfigModule.forRoot(),
    OpeniddictproConfigModule.forRoot(),
    TextTemplateManagementConfigModule.forRoot(),
    IdentityConfigModule.forRoot(),
    ThemeSharedModule.forRoot(),
    SettingManagementConfigModule.forRoot(),
    ThemeLeptonXModule.forRoot({
      defaultTheme: 'light'
    }),
    SideMenuLayoutModule.forRoot(),
    AccountLayoutModule.forRoot(),
    FeatureManagementModule.forRoot(),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.useServiceWorker,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
    ReactiveFormsModule,
    NgxIndexedDBModule.forRoot(dbConfig),
  ],
  providers: [
    APP_ROUTE_PROVIDER,
    {
      provide: APP_INITIALIZER,
      useFactory: faroInitializer,
      deps: [FaroInitializerService],
      multi: true
    },
    {
      provide: CONTENT_SECURITY_STRATEGY,
      useFactory: cspFactory,
    },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService
    },
    provideAbpThemeShared(
      withValidationBluePrint({
        pattern: '::InvalidFormat'
      })
    ),
    AppVersionService,
    FaroInitializerService,
    { provide: HTTP_INTERCEPTORS, useClass: CustomErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RequestDelayInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ServerClockInterceptor, multi: true },
    { provide: LPX_TRANSLATE_SERVICE_TOKEN,  useClass: LeptonXTranslateService },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
